const ExperiencesSection = () => {
  return (
    <section id="experiences" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-primary-600 text-sm uppercase tracking-wider font-semibold mb-4 block">
            What You'll Experience
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
            Mind, Body & Soul Transformation
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our comprehensive approach addresses every aspect of your being through carefully curated practices and experiences.
          </p>
        </div>

        {/* Experience Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          {/* Mind Card */}
          <div className="relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 group h-96">
            <div 
              className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
              style={{
                backgroundImage: `url('https://public.readdy.ai/ai/img_res/39aa58a7c28c3a1f69f1121e661fefa8.jpg')`
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 via-purple-600/40 to-purple-400/30 group-hover:from-purple-900/70"></div>
            <div className="relative z-10 p-8 h-full flex flex-col justify-center text-white text-center">
              <div className="mb-6">
                <i className="ri-brain-line text-5xl mb-4"></i>
              </div>
              <h3 className="text-3xl font-serif font-bold mb-6">Mind</h3>
              <div className="w-16 h-px bg-white/50 mx-auto mb-6"></div>
              <ul className="space-y-3 text-left">
                <li className="flex items-start">
                  <i className="ri-flower-line text-lg mr-3 mt-1 flex-shrink-0"></i>
                  <span>Daily Yoga sessions for focus, clarity, and energy</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-flower-line text-lg mr-3 mt-1 flex-shrink-0"></i>
                  <span>Pranayama & breathing practices to release stress</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-flower-line text-lg mr-3 mt-1 flex-shrink-0"></i>
                  <span>Anahata Focus: Emotional balance and heart-centered living</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Body Card */}
          <div className="relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 group h-96 lg:h-[28rem]">
            <div 
              className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=ayurvedic%20massage%20therapy%20with%20natural%20oils%20traditional%20healing%20treatments%20dosha%20balancing%20sattvic%20healthy%20cuisine%20nourishing%20meals%20physical%20wellness%20body%20alignment%20therapeutic%20spa&width=500&height=700&seq=body001&orientation=portrait')`
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 via-amber-600/40 to-amber-400/30 group-hover:from-amber-900/70"></div>
            <div className="relative z-10 p-8 h-full flex flex-col justify-center text-white text-center">
              <div className="mb-6">
                <i className="ri-body-scan-line text-5xl mb-4"></i>
              </div>
              <h3 className="text-3xl font-serif font-bold mb-6">Body</h3>
              <div className="w-16 h-px bg-white/50 mx-auto mb-6"></div>
              <ul className="space-y-3 text-left">
                <li className="flex items-start">
                  <i className="ri-flower-line text-lg mr-3 mt-1 flex-shrink-0"></i>
                  <span>Ayurvedic therapies to balance doshas</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-flower-line text-lg mr-3 mt-1 flex-shrink-0"></i>
                  <span>Sattvic meals crafted to nourish and energize</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-flower-line text-lg mr-3 mt-1 flex-shrink-0"></i>
                  <span>Gentle mindful movement to align physical energy</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Soul Card */}
          <div className="relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 group h-96">
            <div 
              className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=peaceful%20meditation%20spiritual%20reflection%20creative%20expression%20mindful%20activities%20nature%20connection%20soul%20healing%20transformation%20beach%20sunset%20golden%20hour%20serene%20contemplation%20inner%20peace&width=500&height=600&seq=soul001&orientation=portrait')`
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/80 via-indigo-600/40 to-indigo-400/30 group-hover:from-indigo-900/70"></div>
            <div className="relative z-10 p-8 h-full flex flex-col justify-center text-white text-center">
              <div className="mb-6">
                <i className="ri-heart-3-line text-5xl mb-4"></i>
              </div>
              <h3 className="text-3xl font-serif font-bold mb-6">Soul</h3>
              <div className="w-16 h-px bg-white/50 mx-auto mb-6"></div>
              <ul className="space-y-3 text-left">
                <li className="flex items-start">
                  <i className="ri-flower-line text-lg mr-3 mt-1 flex-shrink-0"></i>
                  <span>Guided meditation and reflection sessions</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-flower-line text-lg mr-3 mt-1 flex-shrink-0"></i>
                  <span>Creative and mindful activities for self-expression</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-flower-line text-lg mr-3 mt-1 flex-shrink-0"></i>
                  <span>Time to recharge and connect with nature</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Who Should Join */}
        <div className="bg-sage-50 rounded-3xl p-12 text-center">
          <h3 className="text-3xl font-serif font-bold text-gray-900 mb-6">Who Should Join</h3>
          <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
            <strong>Anyone seeking balance, energy, and holistic well-being.</strong> Swasthya is your chance to pause, reset, and reconnect with yourself 
            in a nurturing and transformative environment. Whether you're new to Ayurveda or looking to deepen your practice, 
            our retreat welcomes all levels of experience with open hearts.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ExperiencesSection;